import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { VoiceAnalysisStatus } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { useAutoRetry } from '@/composable/use-auto-retry';
import type { TargetTask, VoiceAnalysisManagerOptions } from './voice-analysis-types';

const POLLING_INTERVAL_MS = 45000; // 45秒間隔
const MAX_POLLING_RETRIES = 28; // 28回（45秒 × 28回 = 21分）
const STOP_REASON_MAX_RETRIES = 'max retries reached';

/**
 * 手動分析専用の音声分析マネージャー
 * - ユーザーが「分析」ボタンをクリックして開始
 * - バナー表示あり
 * - transcription_statuses API で id のみ指定してポーリング（バックエンドは execute_method を受け取らない）
 */
export const useManualVoiceAnalysis = (options?: VoiceAnalysisManagerOptions) => {
  const { t } = useI18n();

  const analyzingIds = ref<Set<number>>(new Set());
  const sharedPoller = ref<{ stop: () => void; restart: () => void } | null>(null);
  const bannerStates = ref<Map<number, TargetTask>>(new Map());
  const calleeDidNumbers = ref<Map<number, string | undefined>>(new Map());

  const api = new ZoomPhoneResultApiService();

  // ============================================================================
  // 内部関数
  // ============================================================================

  const setErrorBanner = (
    id: number,
    errorMessageKey: string,
    createdAt?: string,
  ) => {
    const currentTask = bannerStates.value.get(id);
    const calleeDidNumber = calleeDidNumbers.value.get(id);

    bannerStates.value.set(id, {
      id,
      errorData: t(errorMessageKey),
      createdAt: createdAt || currentTask?.createdAt || new Date().toISOString(),
      finishedAt: new Date().toISOString(),
      calleeDidNumber,
    });
  };

  const setSuccessBanner = (id: number) => {
    const currentTask = bannerStates.value.get(id);
    const calleeDidNumber = calleeDidNumbers.value.get(id);

    // NOTE: 既にエラーバナーが設定されている場合は上書きしない
    //       エラーバナーが設定されている場合は成功バナーを表示しない
    if (!currentTask?.errorData) {
      bannerStates.value.set(id, {
        id,
        finishedAt: new Date().toISOString(),
        createdAt: currentTask?.createdAt || new Date().toISOString(),
        calleeDidNumber,
      });
    }
  };

  const setAnalyzingState = (id: number, calleeDidNumber?: string) => {
    const createdAt = new Date().toISOString();

    analyzingIds.value.add(id);
    calleeDidNumbers.value.set(id, calleeDidNumber);
    bannerStates.value.set(id, {
      id,
      createdAt,
      calleeDidNumber,
    });
  };

  const completeAnalysis = (
    id: number,
    status: VoiceAnalysisStatus,
    errorMessageKey?: string,
  ) => {
    analyzingIds.value.delete(id);

    if (errorMessageKey) {
      setErrorBanner(id, errorMessageKey);
    } else if (status === VoiceAnalysisStatus.Succeeded) {
      setSuccessBanner(id);
    }

    calleeDidNumbers.value.delete(id);
    options?.onStatusUpdate?.(id, status);
  };

  const processPollingResults = (
    currentIds: number[],
    statuses: { id: number; voiceAnalysisStatus: VoiceAnalysisStatus }[],
  ): boolean => {
    let allCompleted = true;

    for (const id of currentIds) {
      const result = statuses.find((r) => r.id === id);

      if (!result) {
        allCompleted = false;
        continue;
      }

      const status = result.voiceAnalysisStatus;

      if (status === VoiceAnalysisStatus.Failed) {
        completeAnalysis(id, VoiceAnalysisStatus.Failed, 'vaApp.voiceAnalysis.error.pollingFailed');
        continue;
      }

      if (status === VoiceAnalysisStatus.Succeeded) {
        completeAnalysis(id, VoiceAnalysisStatus.Succeeded);
        continue;
      }

      allCompleted = false;
    }

    return allCompleted;
  };

  /**
   * 手動分析用のポーリング（id のみでステータス取得）
   */
  const executePolling = async (): Promise<boolean> => {
    const currentIds = Array.from(analyzingIds.value);

    if (currentIds.length === 0) {
      return true;
    }

    const response = await api.transcriptionStatusesZoomPhoneResults({
      request: {
        id: currentIds,
      },
    });

    if (!response?.data) {
      return false;
    }

    const data = response.data;
    const statuses = data.statuses;
    if (!statuses) {
      return false;
    }

    return processPollingResults(currentIds, statuses);
  };

  const handlePollingError = () => {
    const currentIds = Array.from(analyzingIds.value);
    currentIds.forEach(id => {
      completeAnalysis(id, VoiceAnalysisStatus.Failed, 'vaApp.voiceAnalysis.error.pollingFailed');
    });
    sharedPoller.value = null;
  };

  const handlePollingStop = (reason?: string) => {
    if (reason === STOP_REASON_MAX_RETRIES) {
      const currentIds = Array.from(analyzingIds.value);
      currentIds.forEach(id => {
        completeAnalysis(id, VoiceAnalysisStatus.Failed, 'vaApp.voiceAnalysis.error.timeout');
      });
    }
    sharedPoller.value = null;
  };

  const startSharedPolling = () => {
    if (sharedPoller.value) {
      sharedPoller.value.stop();
    }

    if (analyzingIds.value.size === 0) {
      sharedPoller.value = null;
      return;
    }

    const { start, stop } = useAutoRetry(
      executePolling,
      {
        interval: POLLING_INTERVAL_MS,
        maxRetries: MAX_POLLING_RETRIES,
        onSuccess: () => {
          sharedPoller.value = null;
        },
        onError: handlePollingError,
        onStop: handlePollingStop,
      },
    );

    sharedPoller.value = { stop, restart: startSharedPolling };
    void start();
  };

  // ============================================================================
  // 外部に提供される関数
  // ============================================================================

  const startAnalysis = async (id: number, calleeDidNumber?: string) => {
    // 重複分析防止
    if (analyzingIds.value.has(id)) {
      return;
    }

    const createdAt = new Date().toISOString();

    try {
      await api.analyzeZoomPhoneResult({
        request: { id },
        errorHandlers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          400: () => {
            setErrorBanner(id, 'vaApp.voiceAnalysis.error.invalidRequest', createdAt);
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          404: () => {
            setErrorBanner(id, 'vaApp.voiceAnalysis.error.notFound', createdAt);
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          409: () => {
            setErrorBanner(id, 'vaApp.voiceAnalysis.error.inProgress', createdAt);
          },
        },
      });
    } catch (_error) {
      // エラーハンドラで処理済みのため、ここでは何もしない
      return;
    }

    setAnalyzingState(id, calleeDidNumber);
    startSharedPolling();
  };

  const dismissBanner = (id: number) => {
    bannerStates.value.delete(id);
  };

  const isAnalyzing = (id: number): boolean => {
    return analyzingIds.value.has(id);
  };

  /**
   * ページ復帰時に手動分析を復元
   * API呼び出しなしでバナーを復元してポーリングを開始
   */
  const restoreAnalysis = (id: number, calleeDidNumber?: string) => {
    // 重複防止
    if (analyzingIds.value.has(id)) {
      return;
    }

    setAnalyzingState(id, calleeDidNumber);
    startSharedPolling();
  };

  const stopAll = () => {
    if (sharedPoller.value) {
      sharedPoller.value.stop();
      sharedPoller.value = null;
    }
    analyzingIds.value.clear();
    bannerStates.value.clear();
    calleeDidNumbers.value.clear();
  };

  onBeforeUnmount(() => {
    stopAll();
  });

  return {
    bannerStates: computed(() => bannerStates.value),
    analyzingIds: computed(() => analyzingIds.value),
    startAnalysis,
    restoreAnalysis,
    dismissBanner,
    isAnalyzing,
    stopAll,
  };
};
